import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { getAircraftAvailabilityWindows } from "../../shared/aircraftAvailabilityApi";
import { normalizeAircraftRegistrations } from "../../shared/aircraftAvailabilityConstraints";
import TimeCalculationService from "./TimeCalculationService";

dayjs.extend(utc);

const RANGE_INCOMPLETE_ERROR_CODE = "AIRCRAFT_AVAILABILITY_RANGE_INCOMPLETE";
const REFRESH_FAILED_ERROR_CODE = "AIRCRAFT_AVAILABILITY_REFRESH_FAILED";
const RANGE_EXTENSION_DAYS = 7;
const MAX_RANGE_REFRESH_ATTEMPTS = 8;

export const AIRCRAFT_AVAILABILITY_REFRESH_FAILED_MESSAGE =
    "暂时无法确认飞机在调整后时间段是否可用，请稍后重试。";

function parseUtc(value) {
    if (!value) return null;
    const parsed = dayjs.utc(value);
    return parsed.isValid() ? parsed : null;
}

export function expandAircraftAvailabilityQueryRange({
    queryStart,
    queryEnd,
    candidateStart,
    candidateEnd,
} = {}) {
    const parsedCandidateStart = parseUtc(candidateStart);
    const parsedCandidateEnd = parseUtc(candidateEnd);
    if (!parsedCandidateStart || !parsedCandidateEnd || !parsedCandidateStart.isBefore(parsedCandidateEnd)) {
        return null;
    }

    const parsedQueryStart = parseUtc(queryStart);
    const parsedQueryEnd = parseUtc(queryEnd);
    const extension = RANGE_EXTENSION_DAYS;
    let expandedStart = parsedQueryStart || parsedCandidateStart.subtract(extension, "day");
    let expandedEnd = parsedQueryEnd || parsedCandidateEnd.add(extension, "day");

    if (!parsedQueryStart || parsedCandidateStart.isBefore(parsedQueryStart)) {
        expandedStart = parsedCandidateStart.subtract(extension, "day");
    }
    if (!parsedQueryEnd || parsedCandidateEnd.isAfter(parsedQueryEnd)) {
        expandedEnd = parsedCandidateEnd.add(extension, "day");
    }

    return {
        queryStart: expandedStart.toISOString(),
        queryEnd: expandedEnd.toISOString(),
    };
}

function withAvailabilitySnapshot(result, snapshot) {
    return {
        ...result,
        aircraftAvailabilitySnapshot: snapshot,
    };
}

function buildRefreshFailureResult(flights, snapshot, cause = null) {
    return withAvailabilitySnapshot({
        flights,
        error: {
            code: REFRESH_FAILED_ERROR_CODE,
            message: AIRCRAFT_AVAILABILITY_REFRESH_FAILED_MESSAGE,
            cause,
        },
    }, snapshot);
}

class AircraftAvailabilityCalculationService {
    /**
     * 使用飞机可用性数据重算航班时间。若候选时间超出已加载范围，自动扩大范围、
     * 重新查询并从同一份原始航班数据重算，避免把内部查询边界暴露给用户。
     */
    static async calculateFlightTimes(
        flights,
        startIndex,
        options = {},
        dependencies = {},
    ) {
        const calculateFlightTimes = dependencies.calculateFlightTimes
            || TimeCalculationService.calculateFlightTimes.bind(TimeCalculationService);
        const fetchAvailabilityWindows = dependencies.fetchAvailabilityWindows
            || getAircraftAvailabilityWindows;
        const registrations = normalizeAircraftRegistrations([
            ...(options.aircraftAvailabilityRegistrations || []),
            ...(flights || []).map((flight) => flight?.aircraft_reg),
        ]);
        let snapshot = {
            windowsByRegistration: options.aircraftAvailabilityWindowsByRegistration || {},
            queryStart: options.aircraftAvailabilityQueryStart || null,
            queryEnd: options.aircraftAvailabilityQueryEnd || null,
        };

        for (let attempt = 0; attempt <= MAX_RANGE_REFRESH_ATTEMPTS; attempt += 1) {
            const result = calculateFlightTimes(flights, startIndex, {
                ...options,
                aircraftAvailabilityWindowsByRegistration: snapshot.windowsByRegistration,
                aircraftAvailabilityQueryStart: snapshot.queryStart,
                aircraftAvailabilityQueryEnd: snapshot.queryEnd,
            });

            if (result?.error?.code !== RANGE_INCOMPLETE_ERROR_CODE) {
                return withAvailabilitySnapshot(result, snapshot);
            }

            const expandedRange = expandAircraftAvailabilityQueryRange({
                queryStart: snapshot.queryStart,
                queryEnd: snapshot.queryEnd,
                candidateStart: result.error.candidateStart,
                candidateEnd: result.error.candidateEnd,
            });
            if (
                attempt === MAX_RANGE_REFRESH_ATTEMPTS
                || registrations.length === 0
                || !expandedRange
            ) {
                return buildRefreshFailureResult(flights, snapshot);
            }

            try {
                const windowsByRegistration = await fetchAvailabilityWindows({
                    registrations,
                    start: expandedRange.queryStart,
                    end: expandedRange.queryEnd,
                    status: "ACTIVE",
                });
                snapshot = {
                    windowsByRegistration: windowsByRegistration || {},
                    queryStart: expandedRange.queryStart,
                    queryEnd: expandedRange.queryEnd,
                };
            } catch (error) {
                return buildRefreshFailureResult(flights, snapshot, error);
            }
        }

        return buildRefreshFailureResult(flights, snapshot);
    }
}

export default AircraftAvailabilityCalculationService;
